import { Room } from 'Interfaces/JobsInterface';
import { observer } from 'mobx-react';

import { ChevronDown, ChevronUp, Edit, Save, X } from 'lucide-react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import classes from './RoomHeaderTemplate.module.scss';
import { useRoomHeaderTemplate } from './useRoomHeaderTemplate';
import { useStore } from 'hooks/useStore';
import JobProcessingProgressBar from 'components/JobProcessingProgressBar';

interface IRoomTemplate {
  room: Room;
  isExpanded: boolean;
  isEditMode: boolean;
  currentRoomInEditMode: string | null;
  onEditClick: (roomId: string) => void;
  onCancelClick: () => void;
  onSaveClick: (roomId: string) => void;
}

// Custom header template for accordion tabs
const RoomHeaderTemplate = ({
  room,
  isExpanded,
  isEditMode,
  currentRoomInEditMode,
  onEditClick,
  onCancelClick,
  onSaveClick
}: IRoomTemplate) => {
  const { JobPasteStore, onEditActionClick } = useRoomHeaderTemplate();
  const { UserStore } = useStore();

  // Check if Redis check is loading for this specific room
  const isRedisCheckLoading = JobPasteStore.isRoomLoading(room.id) && !isEditMode;

  const isCurrentRoomInEditMode = isEditMode && currentRoomInEditMode === room.id;

  // Get current user info
  const currentUser = UserStore.userMetadata?.userInfo;
  const currentUserId = currentUser?.id || currentUser?.email;

  // Create interactive user list for tooltip
  const getActiveUsersList = () => {
    const activeUsers = JobPasteStore.getActiveUsers(room.id);
    const currentUserInList = activeUsers.find(user =>
      user.username === currentUser?.name ||
      user.username === currentUser?.email ||
      user.username === currentUserId?.toString()
    );

    if (currentUserInList) {
      // Current user is in the list - put "You" at the top
      const otherUsers = activeUsers.filter(user =>
        user.username !== currentUserInList.username
      );

      const userList = ['You'];
      if (otherUsers.length > 0) {
        userList.push(...otherUsers.map(user => user.username));
      }

      return userList.join('\n');
    } else {
      // Current user is not in the list - show all users as is
      return activeUsers.map(user => user.username).join('\n');
    }
  };

  return (
    <span className={`${classes.roomHeader} ${isRedisCheckLoading ? classes.loading : ''}`}>
      <div className={classes.roomLeftSection}>
        {/* Color circle with room color */}
        <div className={classes.colorCircle} style={{ backgroundColor: room.color }}></div>

        {/* Room name and code */}
        <div className={classes.roomNameSection}>
          <span className={classes.roomName}>{room.name}</span>
          <span className={classes.roomCode}>({room.shortCode})</span>
        </div>

        {/* Chevron icon to indicate expanded state */}
        <div className={classes.chevronIcon}>
          {isExpanded ? (
            <ChevronUp color={'#ffff'} size={20} />
          ) : (
            <ChevronDown color={'#ffff'} size={20} />
          )}
        </div>
      </div>

      {/* Job Processing Progress Bar - shown when jobs are being processed */}
      <JobProcessingProgressBar
        roomId={room.id}
        compact={true}
        autoHide={true}
        hideDelay={2000}
        className={classes.jobProcessingProgress}
      />

      <div className={classes.roomMiddleSection}>
        <span className={classes.totalTimeLabel}>Total Process Time - </span>
        <div className={classes.timeBoxes}>
          <div className={classes.timeBox}>
            <span className={classes.timeValue}>{room.RoomTimeTotal.days}</span>
            <span className={classes.timeUnit}>days</span>
          </div>
          <div className={classes.timeBox}>
            <span className={classes.timeValue}>{room.RoomTimeTotal.hours}</span>
            <span className={classes.timeUnit}>hours</span>
          </div>
          <div className={classes.timeBox}>
            <span className={classes.timeValue}>{room.RoomTimeTotal.minutes}</span>
            <span className={classes.timeUnit}>mins</span>
          </div>
        </div>
      </div>

      <div className={classes.roomRightSection}>
        <div className={classes.jobsCount}>
          <span>Jobs: {room.jobs.length}</span>
        </div>

        {/* Active Users Count */}
        {JobPasteStore.getActiveUserCount(room.id) > 0 && (
          <div className={classes.activeUsersCount}>
            <Tooltip
              target=".active-users-tooltip"
              position="top"
              className="active-users-tooltip-content"
            />
            <span
              className="active-users-tooltip"
              data-pr-tooltip={getActiveUsersList()}
            >
              Active: {JobPasteStore.getActiveUserCount(room.id)}
            </span>
          </div>
        )}

        {isCurrentRoomInEditMode ? (
          <div className={classes.editButtons}>
            <Button
              icon={
                !JobPasteStore.jobEditSaving ? (
                  <Save size={18} />
                ) : (
                  <ProgressSpinner
                    style={{ width: '25px', height: '25px' }}
                    strokeWidth="8"
                    animationDuration="1s"
                    className={classes.saveIcon}
                  />
                )
              }
              disabled={JobPasteStore.jobEditSaving}
              className={classes.saveButton}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                // Store current scroll position before any state changes
                const currentScrollY = window.scrollY;

                onSaveClick(room.id);
                // Restore scroll position after a brief delay
                setTimeout(() => {
                  window.scrollTo(0, currentScrollY);
                }, 0);
              }}
              label="Save"
              text
            />

            {!JobPasteStore.jobEditSaving && (
              <Button
                icon={<X size={18} />}
                className={classes.cancelButton}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Store current scroll position before any state changes
                  const currentScrollY = window.scrollY;

                  onCancelClick();
                  // Restore scroll position after a brief delay
                  setTimeout(() => {
                    window.scrollTo(0, currentScrollY);
                  }, 0);
                }}
                label="Cancel"
                text
              />
            )}
          </div>
        ) : (
          !isEditMode &&
          !JobPasteStore.savingAllRooms && (
            <>
              <Tooltip
                target=".redis-check-tooltip"
                position="top"
                className="redis-check-tooltip-content"
              />
              {room.jobs.length > 0 && !isRedisCheckLoading && <button
                className={`${classes.editButton} redis-check-tooltip`}
                // data-pr-tooltip="Edit room"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Store current scroll position before any state changes
                  const currentScrollY = window.scrollY;

                  onEditActionClick(room.id, () => {
                    onEditClick(room.id);
                    // Restore scroll position after a brief delay
                    setTimeout(() => {
                      window.scrollTo(0, currentScrollY);
                    }, 0);
                  });
                }}
              >
                <Edit color={'#ffff'} size={18} />
              </button>
              }

              {/* Show loading indicator when room is being loaded */}
              {isRedisCheckLoading && (
                <div className={classes.loadingIndicator}>
                  <ProgressSpinner
                    style={{ width: '20px', height: '20px' }}
                    strokeWidth="4"
                    animationDuration="1s"
                  />
                  <span className={classes.loadingText}>Loading...</span>
                </div>
              )}
            </>
          )
        )}
      </div>
    </span>
  );
};

export default observer(RoomHeaderTemplate);
